import { MUISelect, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm, useWatch } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { useApplicationPermissionFirmwareConstraints } from "../../../../../../hooks/useApplicationPermissionFirmwareConstraints"
import { useDevices } from "../../../../../../hooks/useDevices"
import { digitalManagerApi } from "../../../../../../services/api"
import { FieldRow, FormPage } from "../../../../../components/FormPage"
import { createApplicationPermissionFirmwareConstraintValidationSchema } from "../../../../../components/Helpers"
import { ApplicationPermissionFirmwareConstraintPost } from "../../../../../models/ApplicationPermissionFirmwareConstraint"
import { Guid } from "../../../../../models/CustomType"
import { DeviceGet } from "../../../../../models/Device"

const swVersionSchema = createApplicationPermissionFirmwareConstraintValidationSchema()

const validationSchema = z.object({
  deviceId: z.string().min(1, "Device is required"),
  ...swVersionSchema.shape,
})

type FormValues = z.infer<typeof validationSchema>

export function ApplicationPermissionFirmwareConstraintCreate() {
  const { applicationId, permissionId } = useParams()
  const navigate = useNavigate()
  const returnURL = `/applications/${applicationId}/permissions/${permissionId}#fw-constraints`

  const { devicesList, isLoadingDevices } = useDevices()
  const { applicationPermissionFirmwareConstraints } = useApplicationPermissionFirmwareConstraints(
    applicationId,
    permissionId
  )

  const existingIds = applicationPermissionFirmwareConstraints.map((c) => c.device?.id)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const deviceIdValue = useWatch({ control, name: "deviceId" })

  useEffect(() => {
    reset({
      deviceId: "",
      mainboardMinSoftwareVersion: "",
      hmiMinSoftwareVersion: "",
    })
  }, [reset])

  const onSubmit = (values: FormValues) => {
    const payload: ApplicationPermissionFirmwareConstraintPost = {
      deviceId: values.deviceId as Guid,
      mainboardMinSoftwareVersion: values.mainboardMinSoftwareVersion,
      hmiMinSoftwareVersion: values.hmiMinSoftwareVersion || undefined,
    }
    return digitalManagerApi
      .post(
        `/applications/${applicationId}/permissions/${permissionId}/firmwareConstraints`,
        payload
      )
      .then((res) => {
        if (res.status === StatusCodes.CREATED) {
          toast.success("Firmware Constraint created")
          navigate(returnURL)
        }
      })
      .catch(() => {
        toast.error("Cannot create Firmware Constraint")
      })
  }

  const handleDeviceChange = useCallback(
    (next: Array<string | { id: string }>) => {
      const firstItem = next?.[0]
      const selectedId = typeof firstItem === "string" ? firstItem : firstItem?.id
      setValue("deviceId", selectedId ?? "", { shouldValidate: true, shouldDirty: true })
    },
    [setValue]
  )

  const handleTextFieldChange = useCallback(
    (
      field: ControllerRenderProps<
        FormValues,
        "mainboardMinSoftwareVersion" | "hmiMinSoftwareVersion"
      >
    ) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const renderDeviceSelect = useCallback(
    () => (
      <MUISelect
        id="deviceId"
        name="deviceId"
        placeholder="Select device"
        size={32}
        dropdownLOV={(devicesList ?? []).map((d: DeviceGet) => ({
          id: d.id,
          name: `${d.slaveId} - ${d.name}`,
          isDisabled: existingIds.includes(d.id),
        }))}
        enableSearch
        clearable={false}
        isDisabled={isLoadingDevices}
        selectedItem={deviceIdValue ? [deviceIdValue] : []}
        onChange={handleDeviceChange}
        errors={
          typeof errors.deviceId?.message === "string"
            ? { deviceId: { message: errors.deviceId.message } }
            : undefined
        }
      />
    ),
    [devicesList, existingIds, isLoadingDevices, deviceIdValue, handleDeviceChange, errors.deviceId?.message]
  )

  const renderMainboardField = useCallback(
    (renderProps: {
      field: ControllerRenderProps<FormValues, "mainboardMinSoftwareVersion">
    }) => (
      <MUITextField
        id="mainboardMinSoftwareVersion"
        type="text"
        size={32}
        name={renderProps.field.name}
        value={renderProps.field.value}
        placeholder="XX.XX.XX"
        onChange={handleTextFieldChange(renderProps.field)}
        onBlur={renderProps.field.onBlur}
        errors={
          errors.mainboardMinSoftwareVersion?.message
            ? {
                mainboardMinSoftwareVersion: {
                  message: errors.mainboardMinSoftwareVersion.message,
                },
              }
            : undefined
        }
      />
    ),
    [errors.mainboardMinSoftwareVersion?.message, handleTextFieldChange]
  )

  const renderHmiField = useCallback(
    (renderProps: {
      field: ControllerRenderProps<FormValues, "hmiMinSoftwareVersion">
    }) => (
      <MUITextField
        id="hmiMinSoftwareVersion"
        type="text"
        size={32}
        name={renderProps.field.name}
        value={renderProps.field.value ?? ""}
        placeholder="XX.XX.XX"
        onChange={handleTextFieldChange(renderProps.field)}
        onBlur={renderProps.field.onBlur}
        errors={
          errors.hmiMinSoftwareVersion?.message
            ? { hmiMinSoftwareVersion: { message: errors.hmiMinSoftwareVersion.message } }
            : undefined
        }
      />
    ),
    [errors.hmiMinSoftwareVersion?.message, handleTextFieldChange]
  )

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={!!deviceIdValue && isDirty}
      isValid={isValid}
    >
      <FieldRow label="Device">
        <Controller name="deviceId" control={control} render={renderDeviceSelect} />
      </FieldRow>

      <FieldRow label="Mainboard Min SW Version">
        <Controller
          name="mainboardMinSoftwareVersion"
          control={control}
          render={renderMainboardField}
        />
      </FieldRow>

      <FieldRow label="HMI Min SW Version">
        <Controller
          name="hmiMinSoftwareVersion"
          control={control}
          render={renderHmiField}
        />
      </FieldRow>
    </FormPage>
  )
}
